import React, { FC, useEffect, useState } from 'react';
import { Button, Card } from 'antd';
import { getFormValues } from 'redux-form';
import {
  mapMyPricingFormToSubsciptionChangeRequest,
  MY_PRICING_FORM_NAME,
  MyPricingFormValues,
  validateMyPricingForm,
} from 'src/pages-all/user/user-profile/my-pricing/my-pricing.logic';
import {
  translate,
  translateWithParser,
  translationKeys,
} from 'logic/translations/translations.service';
import { Text } from 'components/text/Text';
import styled from 'styled-components';
import { colors } from 'styles/colors';
import { ElitepaymentPlan } from 'logic/api-models/api-models';
import { useDispatch, useSelector } from 'react-redux';
import {
  getElitePaymentPlans,
  getElitePaymentSeekerInfo,
  getPersonalizedSubscriptionData,
} from 'logic/store/elitetutor/elitetutor.selectors';
import { logError } from 'logic/log/log.logic';
import { SubscriptionSummery } from 'src/pages-all/user/user-profile/my-pricing/components/Summary';
import { elitetutorSlice } from 'logic/store/elitetutor/elitetutor.slice';
import { fontSize } from 'src/styles/theme';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import { SubChangePreviewInfoBox } from 'src/pages-all/user/user-profile/my-pricing/components/SubChangePreview';
import { useParams } from 'react-router-dom';
import { SubscriptionAlenaAddonSummary } from 'src/pages-all/user/user-profile/my-pricing/components/SubscriptionAlenaAddonSummary';
import { navigationSlice } from 'logic/store/navigation/navigation.slice';
import { AppRoute } from 'app/app.route.const';

const FieldHeadline = styled.div``;

const FieldDesciption = styled.div`
  margin: 20px 0 20px 0;
`;

const ContractPeriodInputBox = styled.div`
  display: flex;
  box-shadow: 3px 3px 5px -5px black;
  margin: 20px 0 20px 0;
  background-color: ${colors.white};
  border-radius: 5px;
  flex-wrap: wrap;
`;

const ContractPeriodInputItem = styled.div<{ selected?: boolean; isFlex: boolean }>`
  flex: 1;
  background-color: ${({ selected }) => (selected ? colors.violetElite : colors.white)};
  color: ${({ selected }) => (selected ? colors.white : colors.black)};
  text-align: center;
  font-size: ${fontSize.base};
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  border-radius: 5px;
  margin: ${({ isFlex }) => (!isFlex ? '31px' : '5px')} 5px 5px;
`;

const ContractPeriodInputBadge = styled.div`
  background-color: ${colors.greenBright};
  padding: 0;
  text-align: center;
  display: grid;
  place-items: center;
  font-weight: 600;
  color: ${colors.white};
  min-height: 20px;
  height: 26px;
  border-radius: 5px 5px 0 0;
`;

const ContractPeriodInputContent = styled.div`
  padding: 5px 0 5px 0;
`;

const ContractDescriptionBox = styled.div`
  margin: 10px;
  line-height: normal;
  font-size: 14px;
`;

const FormSelectionContainer = styled.div<{ mobile: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ mobile }) => (mobile ? '65%' : '')};
`;

const FormSelectionCard = styled(Card)`
  margin-top: 10px;
`;

const FlexDescriptionCard = styled(Card)`
  margin-bottom: 20px;
`;

const ConfirmButton = styled(Button)`
  background-color: ${colors.violetElite};
  width: 100%;
  text-align: center;
  border: 0px;
`;

const NoticeContainer = styled.div`
  margin: 10px 0 10px 0;
`;

type props = {
  planlistener?: (planCode: ElitepaymentPlan) => void;
};

export const FormSelection: FC<props> = ({ planlistener }) => {
  const plans: ElitepaymentPlan[] = useSelector(getElitePaymentPlans) || [];
  const [currentSelectedPlan, setCurrentSelectedPlan] = useState<ElitepaymentPlan>();
  const formValues: MyPricingFormValues = useSelector(getFormValues(MY_PRICING_FORM_NAME));
  const dispatch = useDispatch();
  const { minWideDesktop } = useBreakpoints();
  const elitePaymentSeekerInfo = useSelector(getElitePaymentSeekerInfo);
  const activeSubscription = !!elitePaymentSeekerInfo?.liveSubscriptionCount;
  const subscriptionChangeProcess = !elitePaymentSeekerInfo?.subscription?.pendingChange?.activated
    ? elitePaymentSeekerInfo?.subscription?.pendingChange
    : undefined;
  const { token } = useParams<{ token: string }>();
  const personalizedSubscriptionData = useSelector(getPersonalizedSubscriptionData);

  const showAddonSummary = formValues?.plan?.alenaAddon;

  const handleSubmit = () => {
    const formValidationErrors = validateMyPricingForm(formValues);
    if (Object.keys(formValidationErrors).length !== 0) {
      return logError(translate(translationKeys.pricing.selection.formError));
    }

    if (activeSubscription) {
      return dispatch(
        elitetutorSlice.actions.subscriptionChangePreview(
          mapMyPricingFormToSubsciptionChangeRequest(
            formValues,
            elitePaymentSeekerInfo?.subscription?.versionHash
          )
        )
      );
    }

    // Save form values to Redux state before navigation
    dispatch(elitetutorSlice.actions.setPricingFormValues({ formValues }));

    // Prefetch subscription intent data before navigation
    if (token) {
      // If we have a token, use the createSubscriptionIntentWithToken action
      dispatch(
        elitetutorSlice.actions.createSubscriptionIntentWithToken({
          formValues,
          couponCode: personalizedSubscriptionData?.personalizedSubscription?.couponCode,
          token,
        })
      );
    } else {
      // Standard subscription intent creation
      dispatch(
        elitetutorSlice.actions.createSubscriptionIntent({
          formValues,
        })
      );
    }

    // Navigate to payment page immediately - the data will be ready when the page loads
    const paymentPath = token ? `${AppRoute.MyPayment}/${token}` : AppRoute.MyPayment;
    dispatch(navigationSlice.actions.navigateTo({ path: paymentPath }));
  };

  //preSelect
  useEffect(() => {
    handleContractPeriodSelectionClick(plans[0]);
  }, [plans]);

  const handleContractPeriodSelectionClick = (plan: ElitepaymentPlan) => {
    if (planlistener) planlistener(plan);
    setCurrentSelectedPlan(plan);
  };

  const isFlexPlan = (plan: ElitepaymentPlan) => plan.totalBillingCycles === 24;

  //only works for monthly intervall
  const generateContractPeriodSelection = () => {
    return [...plans].reverse().map((plan, i) => (
      <ContractPeriodInputItem
        key={i}
        onClick={() => handleContractPeriodSelectionClick(plan)}
        selected={currentSelectedPlan === plan}
        isFlex={isFlexPlan(plan)}
      >
        {isFlexPlan(plan) && (
          <ContractPeriodInputBadge>
            {translate(translationKeys.pricing.selection.contractPeriod.flexPlan)}
          </ContractPeriodInputBadge>
        )}
        <ContractPeriodInputContent>
          <span>
            {plan?.totalBillingCycles}{' '}
            {plan?.totalBillingCycles === 1
              ? translate(translationKeys.pricing.selection.contractPeriod.month)
              : translate(translationKeys.pricing.selection.contractPeriod.months)}
          </span>
          <ContractDescriptionBox>
            {translate(translationKeys.pricing.selection.contractPeriod.description[i])}
          </ContractDescriptionBox>
        </ContractPeriodInputContent>
      </ContractPeriodInputItem>
    ));
  };

  const renderSubscriptionForm = () => {
    return (
      <>
        <SubChangePreviewInfoBox subChangeInfo={subscriptionChangeProcess} />
        <FieldHeadline>
          <Text size={18} fontWeight={600}>
            {translate(translationKeys.pricing.selection.contractPeriod.headline)}
          </Text>
        </FieldHeadline>
        <FieldDesciption>
          <ContractPeriodInputBox>{generateContractPeriodSelection()}</ContractPeriodInputBox>
        </FieldDesciption>
        <FlexDescriptionCard>
          {translateWithParser(translationKeys.pricing.selection.contractPeriod.flexDescription)}
        </FlexDescriptionCard>
        <SubscriptionSummery />
      </>
    );
  };
  const renderSubmitButton = () => {
    return (
      <>
        <ConfirmButton type={'primary'} onClick={() => handleSubmit()} htmlType="submit">
          <Text size={14} fontWeight={700} color={'white'}>
            {translate(
              activeSubscription
                ? translationKeys.pricing.selection.button.change
                : translationKeys.pricing.selection.button.confirm
            )}
          </Text>
        </ConfirmButton>
        <NoticeContainer>
          <Text size={14} fontWeight={400} color={'blueGray'}>
            {translate(translationKeys.pricing.selection.delayNoticeSEPA)}
          </Text>
        </NoticeContainer>
      </>
    );
  };

  return (
    <FormSelectionContainer mobile={minWideDesktop}>
      {showAddonSummary ? (
        <>
          <FormSelectionCard>{renderSubscriptionForm()}</FormSelectionCard>
          <FormSelectionCard>
            <SubscriptionAlenaAddonSummary />
          </FormSelectionCard>
          <FormSelectionCard>{renderSubmitButton()}</FormSelectionCard>
        </>
      ) : (
        <>
          <FormSelectionCard>
            {renderSubscriptionForm()}
            {renderSubmitButton()}
          </FormSelectionCard>
        </>
      )}
    </FormSelectionContainer>
  );
};
